import type { ReadonlyTupleOf } from '../containers.ts';
import { arrayOf } from '../containers.ts';
import { Style, logNumber, registerObjectClassFormatter } from '../core/logging.ts';
import { identity, isArrayOf, isNumber } from '../identity.ts';
import { tupleCompare } from '../math_core.ts';
import type { Dot } from './measure.ts';
import { Measure } from './measure.ts';
import type { IMeta } from './meta.ts';
import type { Proportion, Quantity, RatioOf } from './quantity.ts';
import { Scalar } from './scalar.ts';
import type { Unit } from './unit.ts';

type Dimensions = 2 | 3 | 4;
// Note: We wish this were ReadonlyTupleOf<number, S>, but TupleOf<number, S> extends that, which
// is the opposite of what we want.
// This suddenly stopped working on 3/29/2024
// type VT<D extends Dimensions> = ReadonlyTupleOf<number, D>;
// https://stackoverflow.com/a/54451598/8107589
type VT<D extends Dimensions> = readonly [...number[]] & { length: D };
// type QT<S> = S extends Scalar<infer Q> ? Q : never;

export type VectorD3<Q extends Quantity> = Vector<Q, 3>;

// A multi-dimensional quantity.
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class Vector<Q extends Quantity, D extends Dimensions> extends Measure<Q, VT<D>> {
  // Can't narrow VectorSize to a specific integer, unfortunately.
  // https://github.com/microsoft/TypeScript/issues/13995#issuecomment-439430612
  static of<Q extends Quantity, D extends Dimensions>(
    dimensions: D,
    units: Unit<Q>,
  ): IMeta<Vector<Q, D>, Q, VT<D>, number[]> {
    return {
      units,
      build: (v) => new Vector<Q, D>(units, v, dimensions),
      guard: isArrayOf(isNumber),
      serialize: identity,
      deserialize: (v) => (v.length === dimensions ? (Object.freeze(v) as VT<D>) : undefined),
    };
  }

  // static from<Q extends Quantity, D extends Dimensions>(
  //   units: Unit<Q>,
  //   value: Vector<Q, VT<D>> | TupleOf<Scalar<Q>, D> | TupleOf<number, D>,
  // ): Vector<Q, VT<D>> {
  //   let raw: Readonly<TupleOf<number, D>>;
  //   if (value instanceof Vector) {
  //     raw = value.raw;
  //   } else {
  //     raw = value.map((v) => (v instanceof Scalar ? v.raw : v / units.rawToUnits)) as Readonly<
  //       TupleOf<number, D>
  //     >;
  //   }
  //   return new Vector(units, raw);
  //   //  value instanceof Vector
  //   //   ? value.inUnits(units)
  //   //   : new Vector<Q, VT<V['length']>>(units, value.map((v) => v / units.rawToUnits) as VT<V['length']>);
  // }

  // static from<Q extends Quantity, D extends Dimensions>(units: Unit<Q>, value: VT<D>): Vector<Q, D>;

  // static from<Q extends Quantity, D extends Dimensions>(
  //   units: Unit<Q>,
  //   value: Vector<Q, D> | ReadonlyTupleOf<Scalar<Q>, D>,
  // ): Vector<Q, D>;

  // static from<Q extends Quantity, D extends Dimensions>(
  //   units: Unit<Q>,
  //   value: ,
  // ): Vector<Q, D>;

  static from<Q extends Quantity, D extends Dimensions>(
    units: Unit<Q>,
    value: VT<D> | Vector<Q, D> | ReadonlyTupleOf<Scalar<Q>, D>,
  ): Vector<Q, D> {
    let raw: VT<D>;
    if (value instanceof Vector) {
      raw = value.raw; // as VT<D>;
    } else {
      raw = value.map((v) =>
        v instanceof Scalar ? v.raw : v / units.rawToUnits,
      ) as unknown as VT<D>;
    }
    return new Vector(units, raw);
  }

  // static from<V extends TupleOf<number, Dimensions>, Q extends Quantity>(
  //   values: V,
  //   units: Unit<Q>,
  // ): Vector<Q, VT<V['length']>>;

  // static from<V extends TupleOf<Scalar<Quantity>, Dimensions>>(
  //   values: V,
  // ): Vector<QT<V[0]>, VT<V['length']>>;

  // static from<V extends TupleOf<number, Dimensions> | TupleOf<Scalar<Quantity>, Dimensions>>(
  //   values: V,
  //   units?: Unit<Quantity>,
  // ): Vector<Quantity, VT<V['length']>> {
  //   type T = VT<V['length']>;
  //   let raws: TupleOf<number, V['length']>;
  //   if (units) {
  //     vs = values as TupleOf<number, V['length']>;
  //   } else {
  //     const scalars = values as TupleOf<Scalar<Quantity>, V['length']>;
  //     units = scalars[0].units;
  //     vs = scalars.map((v) => v.value(units));
  //   }
  //   return new Vector<Quantity, T>(units, vs as TupleOf<number, V['length']>);
  // }

  protected constructor(units: Unit<Q>, raw: VT<D>);
  protected constructor(units: Unit<Q>, raw: number | VT<D>, length: D);
  protected constructor(units: Unit<Q>, raw: number | VT<D>, length?: D) {
    if (isNumber(raw)) {
      raw = arrayOf(length as number, raw, 'readonly') as VT<D>;
    }
    super(units, raw);
  }

  // TODO: This could also be index: Index<D>
  at(index: keyof VT<D> & number) {
    return new Scalar(this.units, this.raw[index]);
  }

  valueAt(index: keyof VT<D> & number, units?: Unit<Q>) {
    units ??= this.units;
    return this.raw[index] * units.rawToUnits;
  }

  rawAt(index: keyof VT<D> & number) {
    return this.raw[index];
  }

  override compare(other: this, fn?: (t: number, o: number) => number): number {
    fn ??= (a, b) => a - b; // numericCompare;
    return tupleCompare(fn)(this.raw as unknown as number[], other.raw as unknown as number[]);
  }

  override toString(decimals = 2): string {
    return `(${this.value()
      .map((q) => logNumber(q, decimals))
      .join(',')})${this.units.symbol}`;
  }

  static buildFromRaw<Q extends Quantity, D extends Dimensions>(units: Unit<Q>, raw: VT<D>) {
    return new Vector<Q, D>(units, raw);
  }

  protected build(units: Unit<Q>, raw: number | VT<D>): this;
  protected build<Q2 extends Quantity>(units: Unit<Q2>, raw: number | VT<D>): Vector<Q2, D>;
  protected build<Q2 extends Quantity>(units: Unit<Q2>, raw: number | VT<D>): this | Vector<Q2, D> {
    return new Vector<Q2, D>(units, raw, this.raw.length);
  }

  protected transform(
    fn: (q: number, a: number, b: number) => number,
    argA?: number | VT<D>,
    argB?: number | VT<D>,
  ) {
    const result = new Array<number>(this.raw.length);
    for (let i = 0; i < result.length; ++i) {
      const a = typeof argA === 'number' ? argA : (argA?.at(i) ?? NaN);
      const b = typeof argB === 'number' ? argB : (argB?.at(i) ?? NaN);
      result[i] = fn(this.raw[i], a, b);
    }
    return Object.freeze(result) as VT<D>;
  }

  protected reduce(fn: (accumulator: number, q: number) => number, initial: number) {
    return this.raw.reduce(fn, initial);
    // return this.raw.reduce(fn, initial);
  }
}

export interface Vector<Q extends Quantity, D extends Dimensions> {
  dot<Q2 extends Quantity>(
    quantity: Measure<Q2, number> | Measure<Q2, VT<D>>,
  ): Vector<Dot<Q, Q2>, D>;
  per(quantity: Scalar<Q> | this, units?: Unit<Proportion>): Vector<Proportion, D>;
  per<Q2 extends Quantity>(
    quantity: Measure<Q2, number> | Measure<Q2, VT<D>>,
  ): Vector<RatioOf<Q, Q2>, D>;
}

registerObjectClassFormatter(Vector, (v: Vector<Quantity, Dimensions>, b) => {
  b.override(Style.subtle, '(');
  v.value().forEach((e, i) => {
    if (i > 0) {
      b.override(Style.subtle, ',');
    }
    b.override(Style.number, logNumber(e, 2));
  });
  b.override(Style.subtle, ')').override(Style.comment, v.units.symbol);
  return { result: 'builder' } as const;
});
